// Hourly volume availability — how much volume has physically arrived at a hub
// (and is therefore available to sort/process) by each hour of a given day.
//
// The model is a DERIVATION of data we already have, not a new data source:
//   - forecastCustomerHub(customer, hub, date)  → that lane's volume for the day
//     (already includes growth, DOW, seasonal, events, and overrides), and
//   - the lh_schedule arrival times for that lane on that weekday, and
//   - the lane's per-load capacity (CustomerLane.loadCapacity).
//
// CAPACITY-AWARE SPLIT (mirrors LogiCast's green/amber model):
//   scheduledCapacity = (#arrival times) × loadCapacity
//   - scheduled (green) = min(forecast, scheduledCapacity), placed at the real
//     arrival times (equal per load).
//   - extra (amber)     = max(0, forecast − scheduledCapacity): volume the
//     scheduled loads can't carry. It's spread across the operational "fill
//     hours" (sortStart..cutoff) and the lane is flagged as needing more loads.
//   - status: "under"  = forecast exceeds scheduled capacity (extra > 0),
//             "over"   = scheduled capacity far exceeds forecast (trucks
//                        running under-filled),
//             "ok"     = capacity roughly matches forecast,
//             "unscheduled" = forecast volume with no arrival times at all.
//   When loadCapacity is unknown we can't judge over/under, so the whole forecast
//   is split equally across the arrival times (status "ok").

import type { Customer, HubEvent, LhScheduleDay } from "../types";
import { addDays, startOfWeekSat } from "../format";
import { forecastCustomerHub, weekOfYearOf } from "./engine";

/** One stored LH schedule row tagged with its lane. Structurally LhLaneRow. */
export interface LaneScheduleRow {
  customerId: string;
  hubId: string;
  day: LhScheduleDay;
}

/** Per-load capacity lookup, keyed `${customerId}|${hubId}`. */
export type CapacityMap = Map<string, number | null | undefined>;

export interface AvailabilityOptions {
  overrides?: Map<string, number>;
  events?: HubEvent[];
  loadCapacity?: CapacityMap;
  sortStart?: number; // first operational fill hour (default 6)
  cutoff?: number; // last operational fill hour (default 22)
}

const DEFAULT_SORT_START = 6;
const DEFAULT_CUTOFF = 22;
// Trucks are "over-scheduled" once average fill drops below this fraction.
const FILL_OK_FLOOR = 0.8;

export type LaneStatus = "ok" | "over" | "under" | "unscheduled";

/** Per-lane coverage: how the day's forecast maps to scheduled load capacity. */
export interface LaneCoverage {
  customerId: string;
  customerName: string;
  forecast: number;
  loads: number; // scheduled arrival count that weekday
  capacity: number | null; // per-load capacity (null if unknown)
  scheduledCapacity: number | null; // loads × capacity
  scheduled: number; // volume placed at real arrival times (green)
  extra: number; // overflow spread across fill hours (amber)
  status: LaneStatus;
  arrivalTimes: string[]; // sorted
  scheduledByHour: number[]; // length 24 — green volume per hour
  extraByHour: number[]; // length 24 — amber volume per hour
}

/** One load landing on the clock. */
export interface Arrival {
  time: string; // "HH:MM"
  hour: number; // 0..23
  customerId: string;
  customerName: string;
  loadIndex: number; // 1-based within the lane's day
  loadCount: number; // total loads that day for the lane
  volume: number; // this load's slice of the lane's scheduled volume
}

/** Volume arriving in one hour, split green (scheduled) vs amber (extra). */
export interface HourBucket {
  scheduled: number;
  extra: number;
  total: number;
}

export interface HourlyAvailability {
  byHour: HourBucket[]; // length 24
  cumulative: number[]; // length 24 — running total available by end of each hour
  arrivals: Arrival[]; // every scheduled load, sorted by time
  lanes: LaneCoverage[]; // per-customer coverage (sorted: problems first)
  scheduledTotal: number;
  extraTotal: number;
  dayTotal: number; // scheduled + extra (== forecastHubDay for the hub/date)
  loads: number; // count of scheduled loads (= arrivals.length)
  needsSchedule: number; // lanes that are under-scheduled or unscheduled
  overScheduled: number; // lanes whose trucks run under-filled
  lhDow: number; // LH weekday used (0=Mon..6=Sun)
}

/** LH schedule indexes weekdays 0=Mon..6=Sun; JS getDay() is 0=Sun..6=Sat. */
export function lhDayOfWeek(dateStr: string): number {
  return (new Date(dateStr + "T12:00:00").getDay() + 6) % 7;
}

/** Hour (0..23) from a "HH:MM" string; NaN-safe → 0. */
function hourOf(time: string): number {
  const h = parseInt(time.slice(0, 2), 10);
  return Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0;
}

/**
 * Slots for the extra (overflow) trucks: every 2 hours from sortStart up to
 * cutoff. Extra forecast is loaded onto full-capacity trucks placed at these
 * slots (08:00, 10:00, …), mirroring how a planner would add linehaul.
 */
function extraSlots(sortStart: number, cutoff: number): number[] {
  const a = Math.min(23, Math.max(0, sortStart));
  const b = Math.min(23, Math.max(a, cutoff));
  const out: number[] = [];
  for (let h = a; h <= b; h += 2) out.push(h);
  return out.length ? out : [a];
}

/**
 * Distribute overflow `extra` as full truck-capacity chunks across `slots`:
 * one truck of `capacity` per slot (the last one partial). If capacity is
 * unknown, the whole overflow goes in a single chunk at the first slot. Any
 * trucks beyond the available slots pile onto the last slot.
 */
function spreadExtra(extra: number, capacity: number | null, slots: number[]): number[] {
  const v = new Array<number>(24).fill(0);
  if (extra <= 0) return v;
  const truck = capacity != null && capacity > 0 ? capacity : extra;
  const trucks = Math.max(1, Math.ceil(extra / truck));
  for (let i = 0; i < trucks; i++) {
    const amt = Math.min(truck, extra - i * truck);
    if (amt <= 0) break;
    v[slots[Math.min(i, slots.length - 1)]] += amt;
  }
  return v;
}

/** Map a lane's forecast onto its scheduled capacity. */
function coverageFor(
  forecast: number,
  loads: number,
  capacity: number | null
): { scheduled: number; extra: number; status: LaneStatus } {
  if (loads === 0) return { scheduled: 0, extra: forecast, status: "unscheduled" };
  if (capacity != null && capacity > 0) {
    const schedCap = loads * capacity;
    const scheduled = Math.min(forecast, schedCap);
    const extra = Math.max(0, forecast - schedCap);
    let status: LaneStatus = "ok";
    if (extra > 0.5) status = "under";
    else if (forecast < schedCap * FILL_OK_FLOOR) status = "over";
    return { scheduled, extra, status };
  }
  // Capacity unknown — place the whole forecast at the scheduled times.
  return { scheduled: forecast, extra: 0, status: "ok" };
}

/** A lane's TOTAL volume per hour (scheduled at its times + extra trucks at slots). */
function laneVector(
  scheduled: number,
  extra: number,
  times: string[],
  capacity: number | null,
  slots: number[]
): number[] {
  const v = new Array<number>(24).fill(0);
  if (times.length > 0 && scheduled > 0) {
    const per = scheduled / times.length;
    for (const t of times) v[hourOf(t)] += per;
  }
  const ev = spreadExtra(extra, capacity, slots);
  for (let h = 0; h < 24; h++) v[h] += ev[h];
  return v;
}

const STATUS_RANK: Record<LaneStatus, number> = { unscheduled: 0, under: 1, over: 2, ok: 3 };

export function hourlyAvailability(
  customers: Customer[],
  lhRows: LaneScheduleRow[],
  hubId: string,
  dateStr: string,
  opts: AvailabilityOptions = {}
): HourlyAvailability {
  const { overrides, events, loadCapacity } = opts;
  const lhDow = lhDayOfWeek(dateStr);
  const slots = extraSlots(opts.sortStart ?? DEFAULT_SORT_START, opts.cutoff ?? DEFAULT_CUTOFF);

  // This hub's lanes that have a schedule row for this weekday, by customer.
  const laneDay = new Map<string, LhScheduleDay>();
  for (const r of lhRows) {
    if (r.hubId === hubId && r.day.dayOfWeek === lhDow) laneDay.set(r.customerId, r.day);
  }

  const byHour: HourBucket[] = Array.from({ length: 24 }, () => ({ scheduled: 0, extra: 0, total: 0 }));
  const arrivals: Arrival[] = [];
  const lanes: LaneCoverage[] = [];
  let scheduledTotal = 0;
  let extraTotal = 0;

  for (const c of customers) {
    const forecast = forecastCustomerHub(c, hubId, dateStr, overrides, events);
    if (forecast <= 0) continue;

    const day = laneDay.get(c.id);
    const times = [...(day?.arrivalTimes ?? [])].sort();
    const capacity = loadCapacity?.get(`${c.id}|${hubId}`) ?? null;
    const cov = coverageFor(forecast, times.length, capacity);
    const schedByHour = new Array<number>(24).fill(0);
    const extraByHour = new Array<number>(24).fill(0);

    // Scheduled volume → placed at the real arrival times (equal per load).
    if (times.length > 0 && cov.scheduled > 0) {
      const per = cov.scheduled / times.length;
      times.forEach((t, i) => {
        const hr = hourOf(t);
        byHour[hr].scheduled += per;
        schedByHour[hr] += per;
        arrivals.push({
          time: t,
          hour: hr,
          customerId: c.id,
          customerName: c.name,
          loadIndex: i + 1,
          loadCount: times.length,
          volume: per,
        });
      });
      scheduledTotal += cov.scheduled;
    }

    // Extra volume → full truck-capacity chunks every 2 hours across the sort window.
    if (cov.extra > 0) {
      const ev = spreadExtra(cov.extra, capacity, slots);
      for (let h = 0; h < 24; h++) {
        if (ev[h] > 0) {
          byHour[h].extra += ev[h];
          extraByHour[h] += ev[h];
        }
      }
      extraTotal += cov.extra;
    }

    lanes.push({
      customerId: c.id,
      customerName: c.name,
      forecast,
      loads: times.length,
      capacity: capacity ?? null,
      scheduledCapacity: capacity != null ? times.length * capacity : null,
      scheduled: cov.scheduled,
      extra: cov.extra,
      status: cov.status,
      arrivalTimes: times,
      scheduledByHour: schedByHour,
      extraByHour,
    });
  }

  // Totals per hour + cumulative.
  const cumulative = new Array<number>(24).fill(0);
  let run = 0;
  for (let h = 0; h < 24; h++) {
    byHour[h].total = byHour[h].scheduled + byHour[h].extra;
    run += byHour[h].total;
    cumulative[h] = run;
  }

  arrivals.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
  lanes.sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.forecast - a.forecast);

  return {
    byHour,
    cumulative,
    arrivals,
    lanes,
    scheduledTotal,
    extraTotal,
    dayTotal: scheduledTotal + extraTotal,
    loads: arrivals.length,
    needsSchedule: lanes.filter((l) => l.status === "under" || l.status === "unscheduled").length,
    overScheduled: lanes.filter((l) => l.status === "over").length,
    lhDow,
  };
}

// ── 5-week detail ─────────────────────────────────────────────────────────────
// For a hub, the hourly arrival pattern across 5 consecutive operational weeks
// (the anchor date's Sat→Fri week + the next 4), broken down three ways:
//   • per week  (week × hour)         — magnitude shift across weeks,
//   • per day within a week (day × hour) — the day-of-week shape,
//   • per customer per week           — which lanes arrive when.
// One pass computes all three so the views stay consistent.

export interface DayDetail {
  date: string;
  byHour: number[]; // length 24
  total: number;
}

export interface WeekDetail {
  weekStart: string;
  weekEnd: string;
  weekOfYear: number;
  byHour: number[]; // length 24 — hub total per hour across the week
  total: number;
  days: DayDetail[]; // 7 days, Sat→Fri
}

export interface CustomerWeekly {
  customerId: string;
  customerName: string;
  weeks: Array<{ weekOfYear: number; byHour: number[]; total: number }>; // 5
  grandTotal: number;
}

export interface FiveWeekDetail {
  weeks: WeekDetail[];
  customers: CustomerWeekly[]; // with volume, sorted by grand total desc
  maxWeekHour: number;
  maxDayHour: number;
  maxCustHour: number;
}

export function fiveWeekDetail(
  customers: Customer[],
  lhRows: LaneScheduleRow[],
  hubId: string,
  anchorDate: string,
  opts: AvailabilityOptions = {}
): FiveWeekDetail {
  const { overrides, events, loadCapacity } = opts;
  const slots = extraSlots(opts.sortStart ?? DEFAULT_SORT_START, opts.cutoff ?? DEFAULT_CUTOFF);
  const week0 = startOfWeekSat(anchorDate);

  // Per-customer accumulator: weeks[5] each a 24-hour vector.
  const custMap = new Map<string, { name: string; weeks: number[][] }>();
  const ensureCust = (c: Customer) => {
    let e = custMap.get(c.id);
    if (!e) {
      e = { name: c.name, weeks: Array.from({ length: 5 }, () => new Array<number>(24).fill(0)) };
      custMap.set(c.id, e);
    }
    return e;
  };

  const weeks: WeekDetail[] = [];
  for (let w = 0; w < 5; w++) {
    const weekStart = addDays(week0, w * 7);
    const weekByHour = new Array<number>(24).fill(0);
    const days: DayDetail[] = [];

    for (let d = 0; d < 7; d++) {
      const date = addDays(weekStart, d);
      const lhDow = lhDayOfWeek(date);
      const laneDay = new Map<string, LhScheduleDay>();
      for (const r of lhRows) {
        if (r.hubId === hubId && r.day.dayOfWeek === lhDow) laneDay.set(r.customerId, r.day);
      }

      const dayByHour = new Array<number>(24).fill(0);
      for (const c of customers) {
        const forecast = forecastCustomerHub(c, hubId, date, overrides, events);
        if (forecast <= 0) continue;
        const times = [...(laneDay.get(c.id)?.arrivalTimes ?? [])].sort();
        const capacity = loadCapacity?.get(`${c.id}|${hubId}`) ?? null;
        const cov = coverageFor(forecast, times.length, capacity);
        const vec = laneVector(cov.scheduled, cov.extra, times, capacity, slots);
        const ce = ensureCust(c);
        for (let h = 0; h < 24; h++) {
          dayByHour[h] += vec[h];
          ce.weeks[w][h] += vec[h];
        }
      }
      for (let h = 0; h < 24; h++) weekByHour[h] += dayByHour[h];
      days.push({ date, byHour: dayByHour, total: dayByHour.reduce((s, v) => s + v, 0) });
    }

    weeks.push({
      weekStart,
      weekEnd: addDays(weekStart, 6),
      weekOfYear: weekOfYearOf(weekStart),
      byHour: weekByHour,
      total: weekByHour.reduce((s, v) => s + v, 0),
      days,
    });
  }

  const customersOut: CustomerWeekly[] = [];
  for (const [id, e] of custMap) {
    const wk = e.weeks.map((bh, i) => ({
      weekOfYear: weeks[i].weekOfYear,
      byHour: bh,
      total: bh.reduce((s, v) => s + v, 0),
    }));
    const grandTotal = wk.reduce((s, x) => s + x.total, 0);
    if (grandTotal > 0) customersOut.push({ customerId: id, customerName: e.name, weeks: wk, grandTotal });
  }
  customersOut.sort((a, b) => b.grandTotal - a.grandTotal);

  return {
    weeks,
    customers: customersOut,
    maxWeekHour: Math.max(1, ...weeks.flatMap((w) => w.byHour)),
    maxDayHour: Math.max(1, ...weeks.flatMap((w) => w.days.flatMap((d) => d.byHour))),
    maxCustHour: Math.max(1, ...customersOut.flatMap((c) => c.weeks.flatMap((w) => w.byHour))),
  };
}

/** The single Sat→Fri week containing `anchorDate`, broken down day × hour (hub totals). */
export function weekDayGrid(
  customers: Customer[],
  lhRows: LaneScheduleRow[],
  hubId: string,
  anchorDate: string,
  opts: AvailabilityOptions = {}
): WeekDetail {
  const weekStart = startOfWeekSat(anchorDate);
  const byHour = new Array<number>(24).fill(0);
  const days: DayDetail[] = [];
  for (let d = 0; d < 7; d++) {
    const date = addDays(weekStart, d);
    const a = hourlyAvailability(customers, lhRows, hubId, date, opts);
    const dayByHour = a.byHour.map((b) => b.total);
    for (let h = 0; h < 24; h++) byHour[h] += dayByHour[h];
    days.push({ date, byHour: dayByHour, total: a.dayTotal });
  }
  return {
    weekStart,
    weekEnd: addDays(weekStart, 6),
    weekOfYear: weekOfYearOf(weekStart),
    byHour,
    total: byHour.reduce((s, v) => s + v, 0),
    days,
  };
}

/** Scheduled linehaul loads arriving at the given hub(s) on a date (that weekday). */
export function lhLoadsInOnDay(
  lhRows: LaneScheduleRow[],
  hubIds: Set<string>,
  dateStr: string
): number {
  const dow = lhDayOfWeek(dateStr);
  let n = 0;
  for (const r of lhRows) {
    if (hubIds.has(r.hubId) && r.day.dayOfWeek === dow) n += r.day.loads;
  }
  return n;
}

// ── All-hubs heatmap ────────────────────────────────────────────────────────
// Hub × hour grid for one date: each hub's total volume arriving in each hour.

export interface HubHourRow {
  hubId: string;
  hubName: string;
  byHour: number[]; // length 24
  total: number;
}

export interface AllHubsHeatmap {
  rows: HubHourRow[];
  maxCell: number;
}

export function allHubsHeatmap(
  customers: Customer[],
  lhRows: LaneScheduleRow[],
  hubs: Array<{ id: string; name: string }>,
  dateStr: string,
  opts: AvailabilityOptions = {}
): AllHubsHeatmap {
  const rows: HubHourRow[] = hubs.map((h) => {
    const a = hourlyAvailability(customers, lhRows, h.id, dateStr, opts);
    return { hubId: h.id, hubName: h.name, byHour: a.byHour.map((b) => b.total), total: a.dayTotal };
  });
  const maxCell = Math.max(1, ...rows.flatMap((r) => r.byHour));
  return { rows, maxCell };
}
